'use client'

import { useIntl } from 'react-intl'
import { Chip, Icon, Text } from '@vinted/web-ui'
import { X16 } from '@vinted/monochrome-icons'
import { has, isEmpty } from 'lodash'

import {
  flattenDynamicFilterOptions,
  removeSelectedFilter,
  removeSelectedStaticFilter,
  DynamicFilterModel,
  SelectedFilter,
  FILTER_PRICE_FROM,
  FILTER_PRICE_TO,
} from '@marketplace-web/catalog/catalog-data'
import { useIsSearchByImageEnabled } from '@marketplace-web/search/search-bar-feature'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { toParams, toUrlQuery } from '@marketplace-web/browser/url-util'
import {
  useCatalogConfigurationContext,
  useCatalogContext,
} from '@marketplace-web/catalog/catalog-page-data'

import useFetchDynamicFilters from '../../../hooks/useFetchDynamicFilters'
import useGetSelectedStaticFilters from '../../../hooks/useGetSelectedStaticFilters'
import useFetchCatalogBrand from '../../../hooks/useFetchCatalogBrand'

import FilterReset from '../FilterReset'
import SearchByImageFilter from '../Filters/SearchByImageFilter'

type StaticFilterClickParam = { filter: SelectedFilter }
type DynamicFilterClickParam = { type: string; id: number }

const SelectedFilters = () => {
  const translate = useTranslate('catalog.selected_filters')
  const intl = useIntl()
  const fetchDynamicFilters = useFetchDynamicFilters()
  const fetchCatalogBrand = useFetchCatalogBrand()
  const {
    state: { dynamicFilters },
  } = useCatalogContext()
  const { selectedStaticFilters } = useGetSelectedStaticFilters()
  const serverConfiguration = useCatalogConfigurationContext()
  const configuration = serverConfiguration
  const { selectedDynamicFilters } = dynamicFilters
  const dynamicFilterList = dynamicFilters.dynamicFilters
  const currency = configuration.config.currency || 'EUR'

  const formatCurrencyValue = (value: string | number) => {
    const numericValue = parseFloat(String(value))

    return intl.formatNumber(numericValue, { style: 'currency', currency })
  }

  const formatLabel = (filter: SelectedFilter) => {
    const { name, label, value } = filter

    if (!isEmpty(label)) return label

    switch (name) {
      case FILTER_PRICE_FROM:
        return translate('price_from', { value: formatCurrencyValue(value) })
      case FILTER_PRICE_TO:
        return translate('price_to', { value: formatCurrencyValue(value) })
      default:
        return name
    }
  }

  const onStaticFilterClick = (...params: [StaticFilterClickParam]) => {
    const { filter } = params[0] as { filter: SelectedFilter }
    const updatedRegularFilters = removeSelectedStaticFilter(filter, selectedStaticFilters)
    const isQueryAvailable = has(updatedRegularFilters, 'query')

    if (isQueryAvailable) {
      const url = new URL(window.location.href)
      const urlParams = toParams(url.search)
      urlParams.search_text = ''

      window.history.replaceState(null, '', `${url.pathname}?${toUrlQuery(urlParams)}`)
    } else {
      fetchDynamicFilters({
        selectedDynamicFilters,
        selectedFilters: updatedRegularFilters,
      })
    }
  }

  const onDynamicFilterClick = (...params: [DynamicFilterClickParam]) => {
    const { type, id } = params[0]
    const updatedDynamicFilters = removeSelectedFilter({ type, id }, selectedDynamicFilters)

    fetchDynamicFilters({
      selectedDynamicFilters: updatedDynamicFilters,
    })
    fetchCatalogBrand({ selectedDynamicFilters: updatedDynamicFilters })
  }

  const handleClick = (filter: SelectedFilter) => () => {
    return filter.isDynamic
      ? onDynamicFilterClick({ type: filter.name, id: Number(filter.value) })
      : onStaticFilterClick({ filter })
  }

  const renderFilter = (filter: SelectedFilter) => {
    const key = filter.isDynamic
      ? `${filter.name}Ids-${filter.value}`
      : `${filter.name}-${filter.value}`
    const text = formatLabel(filter)

    return (
      <div
        key={key}
        className="u-ui-margin-right-regular u-ui-margin-bottom-regular catalog-selected-filter"
      >
        <Chip
          text={text}
          textType={Text.Type.Subtitle}
          suffix={<Icon name={X16} />}
          styling={Chip.Styling.Filled}
          radius={Chip.Radius.Round}
          onClick={handleClick(filter)}
          testId={`catalog--selected-filter-${key}`}
        />
      </div>
    )
  }

  const getSelectedFilters = () => {
    const tempDynamicFilters: Array<SelectedFilter> = []
    const flattedDynamicFilters: Array<DynamicFilterModel> = []

    const tempStaticFilters = selectedStaticFilters

    dynamicFilterList.forEach(filter => {
      flattedDynamicFilters.push({
        ...filter,
        options: flattenDynamicFilterOptions(filter.options),
      })
    })

    selectedDynamicFilters.forEach(selectedFilter => {
      selectedFilter.ids.forEach(id => {
        const dynamicFilter = flattedDynamicFilters.find(
          filter => filter.type === selectedFilter.type,
        )
        const filter = dynamicFilter?.options.find(item => item.id === id)
        if (filter) {
          tempDynamicFilters.push({
            name: selectedFilter.type,
            label: filter.title,
            value: filter.id,
            isDynamic: true,
          })
        }
      })
    })

    return [...tempStaticFilters, ...tempDynamicFilters]
  }

  const isSearchByImageEnabled = useIsSearchByImageEnabled()

  return (
    <div className="u-flexbox u-align-items-center u-flex-wrap">
      {isSearchByImageEnabled && <SearchByImageFilter />}

      {getSelectedFilters().map(renderFilter)}

      <div className="u-margin-left-auto">
        <FilterReset />
      </div>
    </div>
  )
}

export default SelectedFilters
